import React, { useMemo } from 'react';
import { Modal, Button } from 'antd';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { DaySlotGroup } from 'vl-common/src/components/BookAppointment/SlotViewGrid';
import { AppointmentSlot } from 'vl-common/src/schemas/getAppointmentSlots';

const SlotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(100px, 1fr));
  gap: 12px;
  width: 100%;
  max-height: 480px;
  overflow-y: auto;
  overflow-x: auto;
  margin: 12px 0;
  padding-right: 4px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  @media (max-width: 468px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const ModalSlotButton = styled(Button)`
  width: 100%;
  border-radius: 4px;
  font-size: 13px;
  border-color: #d9d9d9;
  color: var(--productPrimaryDark) !important;

  &.ant-btn:hover,
  &.ant-btn:focus,
  &.ant-btn:active {
    border-color: var(--productPrimary) !important;
    color: var(--productPrimaryDark) !important;

    > span {
      color: var(--productPrimaryDark) !important;
    }
  }

  &.ant-btn:focus-visible {
    outline: 2px solid var(--productPrimary);
    outline-offset: 2px;
  }
`;

type DaySlotsModalProps = {
  open: boolean;
  dayGroup: DaySlotGroup | null;
  onClose: () => void;
  onSelectSlot: (slot: AppointmentSlot) => void;
};

export const DaySlotsModal: React.FC<DaySlotsModalProps> = ({ open, dayGroup, onClose, onSelectSlot }) => {
  const { t } = useTranslation('common');

  const sortedSlots = useMemo(() => {
    if (!dayGroup?.slots || dayGroup.slots.length === 0) return [];

    const now = dayjs();

    return [...dayGroup.slots]
      .filter((slot) => dayjs(slot.slot_datetime).isAfter(now))
      .sort((a, b) => dayjs(a.slot_datetime).valueOf() - dayjs(b.slot_datetime).valueOf());
  }, [dayGroup]);

  if (!dayGroup) return null;

  const targetDate = dayGroup.dateKey || dayGroup.dom || sortedSlots[0]?.slot_datetime;
  const parsedDate = dayjs(targetDate);

  const formattedTitleDate = parsedDate.isValid()
    ? parsedDate.format('dddd, D MMMM YYYY')
    : `${dayGroup.dow} ${dayGroup.dom}`;

  return (
    <Modal
      open={open}
      title={t('All available times for {{date}}', { date: formattedTitleDate })}
      onCancel={onClose}
      footer={null}
      centered
      width={520}
      destroyOnHidden
      data-testid="more-slots-modal"
    >
      <SlotsGrid>
        {sortedSlots.map((slot, idx) => {
          const displayTime = dayjs(slot.slot_datetime).format('HH:mm');

          return (
            <ModalSlotButton
              data-testid="select-slot-button"
              key={`modal-slot-${slot.slot_datetime}-${idx}`}
              onClick={() => {
                onSelectSlot(slot);
                onClose();
              }}
            >
              {displayTime}
            </ModalSlotButton>
          );
        })}
      </SlotsGrid>
    </Modal>
  );
};
